/**
 * +field: hero levels. A levelled hero's max HP and power are its ClassDef values × levelMult(level)
 * (compiled gear and talents are already folded into the ClassDef). ClassDef.unlocks gates abilities.
 */
import type { AbilityId, ClassDef, ContentRegistry, Unit } from '../contracts';
import { levelMult } from './level';

/** Clamp a requested hero level to a sane integer (1..60). */
export const clampLevel = (level: number): number => Math.max(1, Math.min(60, Math.floor(level) || 1));

/**
 * Rescale a player unit to `level` from its class def. HP keeps its fraction of max unless `refill`
 * (then HP and every resource that starts above zero — mana, energy — are filled; rage stays).
 */
export function applyHeroLevel(u: Unit, def: ClassDef, level: number, refill: boolean, hpMult = 1): void {
  const lv = clampLevel(level);
  const m = levelMult(lv);
  const frac = u.maxHp > 0 ? u.hp / u.maxHp : 1;
  u.level = lv;
  // +basekits: `hpMult` keeps live maxHpPct auras (Hearthward) across a level-up.
  u.maxHp = Math.max(1, Math.round(def.maxHp * m * hpMult));
  u.stats.power = def.baseStats.power * m;
  if (refill) {
    if (u.alive) u.hp = u.maxHp;
    for (const r of def.resources) {
      const pool = u.resources[r.id];
      // +basekits: a resting resource (Grit) keeps its value, topped up to where it rests.
      if (pool && r.start > 0) pool.cur = r.oocSettle !== undefined ? Math.max(pool.cur, Math.min(pool.max, r.oocSettle)) : pool.max;
    }
  } else if (u.alive) u.hp = Math.max(1, Math.min(u.maxHp, Math.round(u.maxHp * frac)));
}

/**
 * True when a levelled player unit has not unlocked this ability yet (the pressed id or what it
 * resolves to). Unlevelled units and NPCs have everything.
 */
export function abilityLocked(content: Pick<ContentRegistry, 'class'>, u: Unit, pressed: AbilityId, resolved: AbilityId): boolean {
  if (u.kind !== 'player' || u.level === undefined || !u.classId) return false;
  const unlocks = content.class(u.classId)?.unlocks;
  if (!unlocks) return false;
  return (unlocks[pressed] ?? 0) > u.level || (resolved !== pressed && (unlocks[resolved] ?? 0) > u.level);
}
